import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import screenshotBilling from '../assets/restro-lite/screenshot-billing.png';
import screenshotSpeechBill from '../assets/restro-lite/screenshot-speech-bill.png';
import screenshotProducts from '../assets/restro-lite/screenshot-products.png';
import screenshotBills from '../assets/restro-lite/screenshot-bills.png';
import screenshotSales from '../assets/restro-lite/screenshot-sales.png';
import screenshotStock from '../assets/restro-lite/screenshot-stock.png';
import screenshotBillDesign from '../assets/restro-lite/screenshot-bill-design.png';
import screenshotGstCompliance from '../assets/restro-lite/screenshot-gst-compliance.png';
import LiteScreenshotCarousel from '../components/LiteScreenshotCarousel';
import RestroLiteContactForm from '../components/RestroLiteContactForm';
import PocketBillingAiDownloadLinksSection from '../components/PocketBillingAiDownloadLinksSection';
import PageSeo from '../components/PageSeo';
import { pocketBillingAiJsonLd, pocketBillingAiSeo } from '../seo/pocketBillingAiSeo';

const GOOGLE_PLAY_APP_URL =
  'https://play.google.com/store/apps/details?id=in.restroai.lite';

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const BENEFITS = [
  {
    icon: '⚡',
    title: 'Counter-speed billing',
    desc: 'Search products, tap to add, apply discount, and print in seconds. Supports pcs, kg, grams, and litres with one-tap quantity chips.',
  },
  {
    icon: '🎤',
    title: 'AI speech billing',
    desc: 'Speak item names and quantities — hands-free billing. Uses on-device AI (works offline) or optional cloud transcription for best accuracy.',
  },
  {
    icon: '🤖',
    title: 'AI Shop Assistant',
    desc: 'Ask in Hindi or your language about today\'s sales, low stock, udhar dues, and bestsellers — voice or text, with spoken answers.',
  },
  {
    icon: '📈',
    title: 'AI Dashboard',
    desc: 'Fast movers, slow movers, best margin items, and dead stock alerts — filter by today, week, month, or custom dates.',
  },
  {
    icon: '📷',
    title: 'Barcode scanning',
    desc: 'Scan product barcodes with your phone camera to add items instantly — at billing or when setting up your product list.',
  },
  {
    icon: '🧾',
    title: 'GST compliance built in',
    desc: 'GSTR-1 & GSTR-3B style reports, HSN codes, CGST/SGST/IGST split, purchase ITC, vendor masters — export to Excel/PDF for your CA.',
  },
  {
    icon: '📴',
    title: 'Works offline',
    desc: 'Bill customers even when the internet is down. All data stays on your device — no cloud server needed for daily operations.',
  },
  {
    icon: '☁️',
    title: 'Google Drive sync',
    desc: 'Link one Gmail to sync products, bills, and settings between your shop PC and Android phone. Tap Sync when you open or close.',
  },
  {
    icon: '📦',
    title: 'Stock & expiry tracking',
    desc: 'Track quantity on hand, get low-stock alerts, and see products nearing expiry. Stock auto-reduces on every sale.',
  },
  {
    icon: '📒',
    title: 'Udhar (credit) khata',
    desc: 'Record customer dues, partial payments, and overdue accounts. Send udhar bills, payment receipts, and due reminders on WhatsApp or SMS.',
  },
  {
    icon: '📞',
    title: 'AI voice udhar reminders',
    desc: 'Automated phone calls remind credit customers about pending payment — with your shop name and due amount in Hindi, Marathi, Bengali, Tamil, Telugu, Gujarati, or English. Schedule call times, turn on per customer, and track call history.',
  },
  {
    icon: '📊',
    title: 'Sales, profit & reports',
    desc: 'See today\'s revenue, cost, net profit, and payment split (cash vs UPI vs udhar). Export Excel/PDF or share on WhatsApp.',
  },
  {
    icon: '🖨️',
    title: 'Print, WhatsApp & SMS bills',
    desc: 'Professional thermal prints with your shop branding. Share bill PDFs on WhatsApp or SMS — customers get a Google Drive view link.',
  },
  {
    icon: '📸',
    title: 'Product photos on bills',
    desc: 'Snap a photo at checkout — attached to WhatsApp, SMS, and PDF bills (and Google Drive backup). When a customer returns or asks for replacement, open the bill and verify exactly what was sold — clear proof for the shop owner.',
  },
  {
    icon: '📔',
    title: 'Shop diary with photos',
    desc: 'Daily notes for loans, supplier dues, and reminders — attach camera receipts. Link to customers, products, or vendors.',
  },
  {
    icon: '🍽️',
    title: 'Restaurant or retail mode',
    desc: 'Built for kirana shops (products, barcodes, expiry) and cafés/restaurants (menu, recipes, ingredient inventory) — same app, right layout.',
  },
  {
    icon: '🌐',
    title: '22+ Indian languages',
    desc: 'Use the app in Hindi, Marathi, Bengali, Telugu, Tamil, Gujarati, Kannada, Punjabi, Odia, Malayalam, Urdu, and more — switch anytime in Settings.',
  },
  {
    icon: '🔒',
    title: 'Security PIN',
    desc: 'Optional 4-digit PIN so only authorised staff can edit or delete products and bills — peace of mind when you step away.',
  },
];

const SERVICE_CATEGORIES = [
  {
    title: 'Billing & checkout',
    icon: '🛒',
    items: [
      'Fast product search with category filters',
      'Sell by piece, kg, gram, or litre — with quick-pick quantity buttons',
      'Barcode scan to add products at the counter',
      'Custom line items for one-off sales',
      'Bill-level discount, tax %, and service charge',
      'Payment modes: Cash, UPI, Card, Bank transfer, Udhar',
      'Customer name, mobile number, and GSTIN on invoice',
      'One-tap print on thermal or regular printer',
      'Send bill PDF on WhatsApp with a shareable link',
      'Send bill link via SMS when customer has no WhatsApp',
      'Optional product photo on bill — verify returns & replacements from saved bills',
      'Product photos on WhatsApp, SMS & PDF — open from All Bills or Google Drive',
    ],
  },
  {
    title: 'AI Shop Assistant',
    icon: '🤖',
    items: [
      'Ask questions by voice or text — answers read aloud in your language',
      'Hindi, Marathi, Bengali, Telugu, Tamil, Gujarati & 17+ assistant languages',
      'Today, week, month, or all-time sales totals',
      'Low stock, expiry alerts, udhar outstanding, and top sellers',
      'Basic answers offline; sign in with internet for smarter AI replies',
    ],
  },
  {
    title: 'AI Dashboard — stock intelligence',
    icon: '📈',
    items: [
      'Top 10 fast-moving products for 7 days, month, or all time',
      'Slow movers and best margin products',
      'Dead stock — items not sold in 60–180 days',
      'Date filters: today, yesterday, week, month, all, or custom range',
      'Works offline from your local bills and stock history',
    ],
  },
  {
    title: 'Shop diary & receipts',
    icon: '📔',
    items: [
      'Daily shop diary — loans, supplier notes, stock reminders',
      'Attach camera photos to diary entries (bills, receipts, stock)',
      'Link notes to credit customers, products, or GST vendors',
      'Diary photos sync to Google Drive with your shop backup',
    ],
  },
  {
    title: 'AI speech bill',
    icon: '🎤',
    items: [
      'Speak product names and quantities — ideal when hands are busy',
      'On-device Whisper AI — free, works offline after first setup',
      'Optional Groq cloud transcription for highest accuracy',
      'Auto-matches spoken names to your product list',
      'Add more items by voice or fix unmatched lines before checkout',
      'Same checkout flow: print, WhatsApp, or save bill',
    ],
  },
  {
    title: 'GST & tax compliance',
    icon: '🧾',
    items: [
      'GST business profile — GSTIN, state, legal name, address',
      'HSN code and GST rate (0%, 5%, 12%, 18%, 28%) per product',
      'GSTR-1 style outward supply summary (B2B, B2C, interstate)',
      'GSTR-3B style tax liability with CGST, SGST, and IGST split',
      'Customer and vendor masters with GSTIN validation',
      'Record purchase bills and track Input Tax Credit (ITC)',
      'Credit notes and debit notes',
      'Sales & purchase registers for audit',
      'Export reports as Excel, PDF, CSV, or JSON — share on WhatsApp to your CA',
    ],
  },
  {
    title: 'Products, menu & stock',
    icon: '📦',
    items: [
      'Add products with price, category, photo, barcode, and expiry date',
      'Load sample menu (restaurant) or sample products (grocery) in one tap',
      'Purchase price per item for profit calculation',
      'Low-stock alerts and expiry warnings (1 week to 2 months)',
      'Restaurant mode: ingredients, recipes, auto stock deduction per dish',
      'Retail mode: simple quantity-on-hand per product',
      'Stock restores automatically if you delete a bill',
    ],
  },
  {
    title: 'AI voice udhar reminders',
    icon: '📞',
    items: [
      'Automated voice calls to udhar customers with pending balance',
      'Polite reminder script with customer name, shop name, and due amount',
      'Languages: Hindi, English, Gujarati, Marathi, Tamil, Telugu, Bengali',
      'Edit the call message and listen to a preview before it goes live',
      'Schedule call windows — choose morning, afternoon, or evening slots',
      'Enable for all customers or turn on/off per udhar account',
      'Call history and status on each customer\'s udhar profile',
      'Use alongside WhatsApp and SMS due reminders — your choice per customer',
    ],
  },
  {
    title: 'Udhar & customer accounts',
    icon: '📒',
    items: [
      'Open credit accounts for regular customers',
      'Record udhar bills and track outstanding balance',
      'Partial payments with cash, UPI, card, or bank transfer',
      'Status badges: paid, partial, unpaid, overdue',
      'Print udhar invoices and payment receipts',
      'Send due reminders and receipts on WhatsApp or SMS',
      'See total udhar given vs collected in sales reports',
    ],
  },
  {
    title: 'Reports & business insights',
    icon: '📊',
    items: [
      'Sales dashboard: today, yesterday, 7 days, month, or custom range',
      'Revenue, tax, cost of goods, and net profit in one view',
      'Payment mode breakdown — how much came in cash vs UPI vs udhar',
      'Top-selling products and category-wise sales',
      'Export sales report as Excel or PDF',
      'Preview and share reports on WhatsApp',
      'Inventory snapshot export for stock audits',
    ],
  },
  {
    title: 'Bill design & branding',
    icon: '🖨️',
    items: [
      'Customise bill header, footer, and shop details',
      'Toggle columns: S.No, HSN, GST, discount, unit price',
      'Show or hide business name, address, phone, GSTIN',
      'Live preview before you print',
      'Works with thermal receipt printers and A4 printers',
      'Bluetooth thermal printer support on Android',
    ],
  },
  {
    title: 'Sync, devices & security',
    icon: '☁️',
    items: [
      'Desktop app for Windows and Mac — one-click launcher',
      'Android app for phone and tablet — fully offline on device',
      'UI in 22+ Indian languages — Hindi, Marathi, Bengali, Telugu, Tamil, and more',
      'Google Drive sync: same Gmail on PC and phone',
      'Sync products, bills, settings, stock, and inventory',
      'Smarter merge — newer data wins, deletes stay deleted',
      'Optional 4-digit security PIN for edits and deletes',
      'Your data stays on your devices — you own it',
    ],
  },
];

const SCREENSHOTS = [
  {
    src: screenshotBilling,
    title: 'New bill — fast checkout',
    caption: 'Search or scan products, set qty in kg/pcs, apply discount, choose payment mode, and print or WhatsApp the bill.',
  },
  {
    src: screenshotSpeechBill,
    title: 'AI speech bill',
    caption: 'Speak "2 kg atta, 1 litre oil" — AI matches your products and builds the bill. Works offline with on-device AI.',
  },
  {
    src: screenshotProducts,
    title: 'Products',
    caption: 'Add, edit, and manage your product list with images, buy/sell prices, stock levels, and expiry filters.',
  },
  {
    src: screenshotBills,
    title: 'All bills',
    caption: 'Search and filter bills by date, view counter and udhar sales, and edit past bills with full revision history.',
  },
  {
    src: screenshotSales,
    title: 'Sales & profit',
    caption: 'Revenue, tax, cost, net profit, payment split, and udhar breakdown — export Excel/PDF or share on WhatsApp.',
  },
  {
    src: screenshotStock,
    title: 'Product stock',
    caption: 'Track quantity on hand, low-stock and out-of-stock alerts, expiry dates, and automatic deduction on every sale.',
  },
  {
    src: screenshotBillDesign,
    title: 'Bill design',
    caption: 'Customise printed bills — header, columns, GST fields, totals, and live preview with your shop name.',
  },
  {
    src: screenshotGstCompliance,
    title: 'GST compliance',
    caption: 'GSTR-1 & GSTR-3B style reports, tax liability, ITC tracking, and export to Excel/PDF for your CA.',
  },
];

const IDEAL_FOR = [
  'Kirana & general stores',
  'Grocery & provision shops',
  'Cafés & quick-service counters',
  'Sweet shops & bakeries',
  'Vegetable & fruit vendors',
  'Medical & chemist shops (with expiry tracking)',
  'Small restaurants & dhabas',
  'Hardware & stationery shops',
];

const PRICING_PLANS = [
  {
    id: 'monthly',
    name: '1 Month',
    price: 500,
    period: 'per month',
    perMonth: 500,
    savings: null,
    badge: null,
    popular: false,
  },
  {
    id: 'half-year',
    name: '6 Months',
    price: 2500,
    period: 'for 6 months',
    perMonth: Math.round(2500 / 6),
    savings: 'Save ₹500 vs monthly',
    badge: 'Popular',
    popular: true,
  },
  {
    id: 'yearly',
    name: '1 Year',
    price: 5000,
    period: 'for 12 months',
    perMonth: Math.round(5000 / 12),
    savings: 'Save ₹1,000 vs monthly',
    badge: 'Best value',
    popular: false,
    highlight: true,
  },
];

const PRICING_FEATURES = [
  'Unlimited bills & products',
  'Desktop (Windows/Mac) + Android app on Google Play',
  '22+ Indian languages (Hindi, Marathi, Bengali, Telugu, Tamil & more)',
  'Offline billing — no internet needed daily',
  'AI speech billing (on-device + optional cloud)',
  'AI Shop Assistant — voice Q&A on sales, stock & udhar',
  'AI Dashboard — fast/slow movers, margins & dead stock',
  'Barcode scanning',
  'GST compliance & GSTR-style reports',
  'Google Drive sync between devices',
  'Sales, profit & inventory reports',
  'AI voice udhar reminders — automated payment reminder calls',
  'Udhar / credit accounts + WhatsApp & SMS reminders',
  'Bill print, WhatsApp PDF & SMS bill link',
  'Product photos on bills — proof for returns & replacements',
  'Shop diary with camera receipts',
  'Bill design & thermal printer support',
  'Security PIN & email support',
];

const HOW_IT_WORKS = [
  {
    step: '1',
    title: 'We set up your shop',
    desc: 'Fill the form below — our team onboards you within 24 hours with your shop name, products, and restaurant or retail mode.',
  },
  {
    step: '2',
    title: 'Install on PC & phone',
    desc: 'Download PocketBillingAI from Google Play for Android, or get the desktop app for your counter. Sign in with your registered mobile — both work offline.',
  },
  {
    step: '3',
    title: 'Bill, track & grow',
    desc: 'Start billing in seconds. Sync with Google Drive, check sales & profit daily, and share GST reports with your CA.',
  },
];

const STATS = [
  { number: '500+', label: 'Shops', icon: '🏪' },
  { number: '40%', label: 'Revenue Increase', icon: '📈' },
  { number: '24/7', label: 'AI Support', icon: '🤖' },
];

const TESTIMONIALS = [
  {
    shop: 'Abhi Kirana',
    city: 'Varanasi',
    owner: 'Abhishek T.',
    role: 'General store owner',
    content:
      'Billing is now 2× faster. Barcode scan, speech bill, and WhatsApp PDF — customers love it. GST reports go straight to my CA every month.',
    rating: 5,
  },
  {
    shop: 'Sharma Medical & General',
    city: 'Lucknow',
    owner: 'Priya Sharma',
    role: 'Medical store',
    content:
      'Expiry tracking saved us from dead stock. Low-stock alerts tell us what to reorder before shelves go empty. Works offline when power cuts happen.',
    rating: 5,
  },
  {
    shop: 'Royal Snacks Corner',
    city: 'Patna',
    owner: 'Vikash Kumar',
    role: 'Snack shop',
    content:
      'Udhar khata is a lifesaver — every customer due is tracked. Partial payments and WhatsApp reminders mean we collect faster than our old notebook.',
    rating: 5,
  },
  {
    shop: 'Tech Park Mini Mart',
    city: 'Bangalore',
    owner: 'Priya Nair',
    role: 'Kirana chain',
    content:
      'PC at counter + phone for floor staff. Google Drive sync keeps both devices identical. Profit report shows real margin, not just sales.',
    rating: 5,
  },
  {
    shop: 'FC Road Provisions',
    city: 'Pune',
    owner: 'Siddharth D.',
    role: 'Provisions store',
    content:
      'Switched from Excel and calculator in one weekend. Team learned billing in under an hour. Support helped us import 400 products with barcodes.',
    rating: 5,
  },
  {
    shop: 'Nawab Daily Needs',
    city: 'Hyderabad',
    owner: 'Farhan Ahmed',
    role: 'Kirana & staples',
    content:
      'GSTR-style reports and HSN on every product — my accountant said this is the cleanest data he has seen from a small shop. Worth every rupee.',
    rating: 5,
  },
];

function PublicNav() {
  return (
    <header className="relative z-50 border-b border-stone-200/80 bg-white/70 backdrop-blur-sm">
      <div className="max-w-6xl mx-auto px-3 sm:px-6 py-2.5 sm:py-4">
        <div className="flex items-center justify-between gap-2">
          <Link to="/" className="flex items-center gap-2 min-w-0">
            <img src="/pocketbillingai-logo.png" alt="Pocket Billing AI" className="h-10 w-10 sm:h-11 sm:w-11 object-contain" />
          </Link>
          <nav className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
            <Link
              to="/"
              className="hidden sm:inline-flex text-xs sm:text-sm font-medium text-stone-700 hover:text-stone-900 px-3 py-2 rounded-full border border-stone-200 hover:border-stone-300"
            >
              Home
            </Link>
            <Link
              to="/restaurants"
              className="text-xs sm:text-sm font-medium text-stone-700 hover:text-stone-900 px-2.5 sm:px-3 py-1.5 sm:py-2 rounded-full border border-stone-200 hover:border-stone-300 whitespace-nowrap"
            >
              Restaurants
            </Link>
            <Link
              to="/pocketbillingai/download"
              className="hidden sm:inline-flex text-xs sm:text-sm font-medium text-stone-700 hover:text-stone-900 px-2.5 sm:px-3 py-1.5 sm:py-2 rounded-full border border-stone-200 hover:border-stone-300 whitespace-nowrap"
            >
              Download
            </Link>
            <a
              href={GOOGLE_PLAY_APP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden md:inline-flex text-xs sm:text-sm font-medium text-stone-800 hover:text-indigo-700 px-2.5 sm:px-3 py-1.5 sm:py-2 rounded-full border border-stone-200 hover:border-indigo-200 whitespace-nowrap"
            >
              ▶ Google Play
            </a>
            <a
              href="#contact"
              className="text-xs sm:text-sm font-semibold text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-indigo-600 hover:bg-indigo-700 shadow shadow-indigo-500/25 whitespace-nowrap"
            >
              Get started
            </a>
          </nav>
        </div>
      </div>
    </header>
  );
}

const SEO_FAQ = [
  {
    q: 'What is PocketBillingAI?',
    a: 'PocketBillingAI is billing and inventory software for Indian kirana shops, retail stores, and small restaurants. It works offline on desktop (Windows/Mac) and Android with GST reports, udhar khata, stock tracking, and AI speech billing.',
  },
  {
    q: 'Does PocketBillingAI work without internet?',
    a: 'Yes. PocketBillingAI is offline-first — bill customers even when the internet is down. Optional Google Drive sync backs up your shop data between PC and phone.',
  },
  {
    q: 'Is PocketBillingAI GST compliant?',
    a: 'Yes. It supports HSN codes, CGST/SGST/IGST, GSTR-1 and GSTR-3B style reports, and Excel/PDF export for your CA.',
  },
  {
    q: 'How much does PocketBillingAI cost?',
    a: 'Plans start from ₹500/month with every feature included. Fill the form on this page to start a free trial — our team onboards your shop within 24 hours.',
  },
  {
    q: 'Where can I download PocketBillingAI for Android?',
    a: 'PocketBillingAI is on Google Play. Install from the Play Store, then sign in with the mobile number registered by our team.',
  },
  {
    q: 'Does PocketBillingAI call udhar customers for payment reminders?',
    a: 'Yes. AI voice udhar reminders place automated phone calls with your shop name and due amount. Choose Hindi, Marathi, Bengali, Tamil, Telugu, Gujarati, or English, schedule call windows, and enable per customer — alongside WhatsApp and SMS reminders.',
  },
  {
    q: 'Can I attach product photos on bills for returns?',
    a: 'Yes. Attach a product photo at checkout — it appears on WhatsApp, SMS, and PDF bills. When a customer asks for return or replacement, open the saved bill to verify what was sold.',
  },
];

export default function RestroLitePage() {
  return (
    <div className="min-h-screen bg-stone-50 text-stone-900 overflow-hidden">
      <PageSeo {...pocketBillingAiSeo} jsonLd={pocketBillingAiJsonLd()} />
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-b from-indigo-100/50 via-transparent to-transparent" />
        <div className="absolute top-0 right-0 w-[70vmax] h-[70vmax] bg-sky-200/40 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/3" />
        <div className="absolute bottom-0 left-0 w-[50vmax] h-[50vmax] bg-violet-200/30 rounded-full blur-[100px] translate-y-1/2 -translate-x-1/3" />
      </div>

      <PublicNav />

      <main className="relative z-10">
        {/* Hero */}
        <section className="max-w-6xl mx-auto px-3 sm:px-6 pt-10 sm:pt-16 pb-12 sm:pb-20">
          <motion.div
            initial="hidden"
            animate="show"
            variants={{ show: { transition: { staggerChildren: 0.08 } } }}
            className="text-center max-w-3xl mx-auto"
          >
            <motion.div variants={fadeUp} className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs sm:text-sm font-semibold mb-3">
              <span>🎉</span> Now on Google Play — Android app live
            </motion.div>
            <motion.div variants={fadeUp} className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-indigo-50 border border-indigo-200 text-indigo-800 text-xs sm:text-sm font-semibold mb-6">
              <span>⚡</span> PocketBillingAI — Billing software for Shops
            </motion.div>
            <motion.h1
              variants={fadeUp}
              className="text-3xl sm:text-5xl md:text-6xl font-bold tracking-tight text-stone-900 leading-[1.1] mb-4 sm:mb-6"
            >
              <span className="block text-indigo-700 text-2xl sm:text-3xl md:text-4xl mb-2 sm:mb-3">
                PocketBillingAI
              </span>
              Bill faster, track profit, stay{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 via-violet-600 to-sky-600">
                GST-ready
              </span>
            </motion.h1>
            <motion.p variants={fadeUp} className="text-base sm:text-lg text-stone-600 mb-8 max-w-2xl mx-auto">
              The complete billing app for kirana shops, cafés, and small restaurants — AI speech billing, AI Shop
              Assistant, AI Dashboard, AI voice udhar reminders, product photos on bills for returns &amp; replacements, shop diary with receipts, barcode scan, udhar khata, stock
              tracking, and GSTR reports. Hindi, Marathi, Bengali, Telugu, Tamil, and 17+ more Indian languages. Works
              offline on your PC and{' '}
              <a
                href={GOOGLE_PLAY_APP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-indigo-600 font-medium hover:underline"
              >
                Android on Google Play
              </a>
              .
            </motion.p>
            <motion.div variants={fadeUp} className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href={GOOGLE_PLAY_APP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full sm:w-auto inline-flex justify-center items-center gap-2 px-8 py-3.5 rounded-xl bg-stone-900 hover:bg-stone-800 text-white font-semibold shadow-lg shadow-stone-900/20 transition-all"
              >
                <span aria-hidden className="text-lg">▶</span>
                Get it on Google Play
              </a>
              <a
                href="#contact"
                className="w-full sm:w-auto inline-flex justify-center items-center px-8 py-3.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold shadow-lg shadow-indigo-500/30 transition-all"
              >
                Start free trial
              </a>
              <a
                href="#services"
                className="w-full sm:w-auto inline-flex justify-center items-center px-8 py-3.5 rounded-xl border border-stone-300 bg-white hover:bg-stone-50 text-stone-800 font-semibold transition-all"
              >
                See all features
              </a>
            </motion.div>
            <motion.p variants={fadeUp} className="mt-6 text-sm text-stone-500">
              <a
                href={GOOGLE_PLAY_APP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-indigo-600 hover:underline font-medium"
              >
                Download on Google Play
              </a>
              {' · '}
              Desktop (Windows/Mac) · Offline-first · From ₹500/month
            </motion.p>
            <motion.div
              variants={fadeUp}
              className="mt-6 flex flex-wrap justify-center items-center gap-4 sm:gap-6 text-stone-500 text-xs sm:text-sm"
            >
              <span className="inline-flex items-center gap-1.5">
                <span className="text-base">✅</span> Trusted by 500+ shops
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="text-base">📴</span> Works offline
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="text-base">🧾</span> GST-ready billing
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="text-base">▶</span>{' '}
                <a
                  href={GOOGLE_PLAY_APP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-indigo-600"
                >
                  On Google Play
                </a>
              </span>
            </motion.div>
          </motion.div>
        </section>

        {/* Stats */}
        <section className="max-w-6xl mx-auto px-3 sm:px-6 pb-12 sm:pb-16">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
            {STATS.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.08 }}
                viewport={{ once: true }}
                className="text-center p-4 sm:p-5 md:p-6 rounded-2xl bg-white border border-stone-200 shadow-sm hover:shadow-md hover:border-indigo-200 transition-all"
              >
                <div className="text-2xl sm:text-3xl mb-2">{stat.icon}</div>
                <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-violet-600 mb-1">
                  {stat.number}
                </div>
                <div className="text-stone-500 text-xs sm:text-sm font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Download */}
        <section id="download" className="max-w-6xl mx-auto px-3 sm:px-6 pb-14 sm:pb-20 scroll-mt-20">
          <PocketBillingAiDownloadLinksSection compact showViewAll downloadPagePath="/pocketbillingai/download" />
        </section>

        {/* Pricing */}
        <section id="pricing" className="max-w-6xl mx-auto px-3 sm:px-6 pb-16 sm:pb-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="text-center mb-8 sm:mb-10">
              <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-100 border border-amber-300 text-amber-900 text-xs sm:text-sm font-bold uppercase tracking-wide mb-4 animate-pulse">
                🔥 Limited time offer — lock in these prices today
              </span>
              <h2 className="text-2xl sm:text-4xl font-bold text-stone-900 mb-3">
                Simple plans, every feature included
              </h2>
              <p className="text-stone-600 max-w-2xl mx-auto text-sm sm:text-base">
                All plans include the full PocketBillingAI app — billing, GST, speech AI, 22+ Indian languages, sync, and support.
                Pay monthly or save more with 6-month and yearly plans.{' '}
                <strong className="text-stone-800">Prices include tax. No hidden fees.</strong>
              </p>
            </div>

            <div className="grid sm:grid-cols-3 gap-4 sm:gap-5 mb-8 sm:mb-10">
              {PRICING_PLANS.map((plan) => (
                <div
                  key={plan.id}
                  className={`relative rounded-2xl sm:rounded-3xl border-2 p-6 sm:p-7 flex flex-col transition-all ${
                    plan.highlight
                      ? 'border-violet-400 bg-gradient-to-br from-violet-50 via-white to-indigo-50 shadow-xl shadow-violet-500/15 scale-[1.02] sm:scale-105 z-10'
                      : plan.popular
                        ? 'border-indigo-300 bg-gradient-to-br from-indigo-50/80 to-white shadow-lg shadow-indigo-500/10'
                        : 'border-stone-200 bg-white shadow-sm hover:border-indigo-200 hover:shadow-md'
                  }`}
                >
                  {plan.badge ? (
                    <span
                      className={`absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-xs font-bold whitespace-nowrap ${
                        plan.highlight
                          ? 'bg-violet-600 text-white shadow-md'
                          : 'bg-indigo-600 text-white shadow-md'
                      }`}
                    >
                      {plan.badge}
                    </span>
                  ) : null}
                  <p className="text-sm font-semibold text-stone-500 uppercase tracking-wider mb-1 mt-1">
                    {plan.name}
                  </p>
                  <p className="text-3xl sm:text-4xl font-bold text-stone-900">
                    ₹{plan.price.toLocaleString('en-IN')}
                  </p>
                  <p className="text-sm text-stone-500 mt-1">{plan.period}</p>
                  {plan.perMonth < 500 ? (
                    <p className="text-xs sm:text-sm text-emerald-700 font-medium mt-2">
                      ≈ ₹{plan.perMonth}/month
                    </p>
                  ) : (
                    <p className="text-xs sm:text-sm text-stone-400 mt-2">Billed monthly</p>
                  )}
                  {plan.savings ? (
                    <p className="mt-3 inline-flex self-start px-2.5 py-1 rounded-lg bg-emerald-100 text-emerald-800 text-xs font-semibold">
                      {plan.savings}
                    </p>
                  ) : (
                    <p className="mt-3 text-xs text-stone-400">Try risk-free — upgrade anytime</p>
                  )}
                  <a
                    href="#contact"
                    className={`mt-6 block w-full text-center py-3 rounded-xl font-semibold text-sm transition-all ${
                      plan.highlight
                        ? 'bg-violet-600 hover:bg-violet-700 text-white shadow-lg shadow-violet-500/25'
                        : plan.popular
                          ? 'bg-indigo-600 hover:bg-indigo-700 text-white shadow-md'
                          : 'bg-stone-100 hover:bg-stone-200 text-stone-800 border border-stone-200'
                    }`}
                  >
                    Get started
                  </a>
                </div>
              ))}
            </div>

            <div className="max-w-3xl mx-auto rounded-2xl border border-amber-200 bg-amber-50/80 px-5 py-4 text-center mb-8 sm:mb-10">
              <p className="text-sm sm:text-base text-amber-900 font-medium">
                ⏳ <strong>Limited time offer</strong> — 6-month and yearly plans are at special launch pricing.
                Grab your plan now before prices go up. Our team will help you choose the best option when you sign up.
              </p>
            </div>

            <div className="max-w-2xl mx-auto rounded-3xl border border-stone-200 bg-white p-6 sm:p-8 shadow-sm">
              <p className="text-center text-stone-700 text-sm font-semibold uppercase tracking-widest mb-4">
                All plans include
              </p>
              <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2.5">
                {PRICING_FEATURES.map((item) => (
                  <li key={item} className="flex items-start gap-2.5 text-sm text-stone-600">
                    <span className="flex-shrink-0 text-emerald-600 font-bold mt-0.5">✓</span>
                    {item}
                  </li>
                ))}
              </ul>
              <a
                href="#contact"
                className="mt-6 block w-full text-center py-3.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold shadow-lg shadow-indigo-500/25 transition-all"
              >
                Fill the form below — we&apos;ll set up your shop within 24 hours
              </a>
            </div>
          </motion.div>
        </section>

        {/* Benefits grid */}
        <section className="bg-white/60 border-y border-stone-200/80 py-14 sm:py-20">
          <div className="max-w-6xl mx-auto px-3 sm:px-6">
            <div className="text-center mb-10 sm:mb-14">
              <h2 className="text-2xl sm:text-4xl font-bold text-stone-900 mb-3">
                Why shop owners choose PocketBillingAI
              </h2>
              <p className="text-stone-600 max-w-2xl mx-auto">
                Everything you need at the counter — not bloated ERP software you will never use. Built for Indian shops
                that want speed, control, and GST compliance without the enterprise price tag.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
              {BENEFITS.map((b, i) => (
                <motion.div
                  key={b.title}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.04 }}
                  className={`rounded-2xl border border-stone-200/80 bg-white p-5 sm:p-6 shadow-sm hover:shadow-md hover:border-indigo-200/80 transition-all${
                    i === BENEFITS.length - 1
                      ? ' col-span-full justify-self-center w-full sm:w-[calc((100%-1.5rem)/2)] lg:w-[calc((100%-3rem)/3)] xl:w-[calc((100%-4.5rem)/4)]'
                      : ''
                  }`}
                >
                  <span className="text-3xl mb-3 block" aria-hidden>
                    {b.icon}
                  </span>
                  <h3 className="font-semibold text-stone-900 mb-2">{b.title}</h3>
                  <p className="text-sm text-stone-600 leading-relaxed">{b.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section id="testimonials" className="max-w-6xl mx-auto px-3 sm:px-6 py-14 sm:py-20 scroll-mt-20">
          <motion.div
            className="text-center mb-8 sm:mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-indigo-600 text-xs sm:text-sm font-semibold tracking-widest uppercase mb-2">
              Shop owner stories
            </p>
            <h2 className="text-2xl sm:text-4xl font-bold text-stone-900 mb-3">
              Loved by kirana & retail shops across India
            </h2>
            <p className="text-stone-600 max-w-2xl mx-auto text-sm sm:text-base">
              Real feedback from owners who bill faster, track stock smarter, and stay GST-ready with PocketBillingAI.
            </p>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
            {TESTIMONIALS.map((item, index) => (
              <motion.div
                key={`${item.shop}-${item.city}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.06 }}
                className="rounded-2xl border border-stone-200 bg-white p-5 sm:p-6 shadow-sm hover:shadow-md hover:border-indigo-200/80 transition-all flex flex-col"
              >
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xl" aria-hidden>
                    🏪
                  </span>
                  <span className="text-indigo-600 text-xs font-semibold uppercase tracking-wide">
                    {item.city}
                  </span>
                </div>
                <h3 className="font-semibold text-stone-900 text-base sm:text-lg">{item.shop}</h3>
                <p className="text-stone-500 text-xs sm:text-sm mb-3">
                  {item.owner} · {item.role}
                </p>
                <div className="flex mb-3" aria-label={`${item.rating} out of 5 stars`}>
                  {[...Array(item.rating)].map((_, i) => (
                    <span key={i} className="text-amber-400 text-sm">
                      ⭐
                    </span>
                  ))}
                </div>
                <p className="text-stone-600 text-sm leading-relaxed italic flex-1">
                  &ldquo;{item.content}&rdquo;
                </p>
              </motion.div>
            ))}
          </div>
          <motion.p
            className="text-center text-stone-500 text-sm mt-8"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            Kirana stores, medical shops, cafés, and small restaurants in Varanasi, Lucknow, Patna, Bangalore, Pune,
            Hyderabad & more trust PocketBillingAI every day.
          </motion.p>
        </section>

        {/* Detailed services */}
        <section id="services" className="max-w-6xl mx-auto px-3 sm:px-6 py-14 sm:py-20 scroll-mt-20">
          <div className="text-center mb-10 sm:mb-14">
            <p className="text-indigo-600 text-xs sm:text-sm font-semibold tracking-widest uppercase mb-2">
              Full feature list
            </p>
            <h2 className="text-2xl sm:text-4xl font-bold text-stone-900 mb-3">
              Every service, explained clearly
            </h2>
            <p className="text-stone-600 max-w-2xl mx-auto">
              Whether you run a kirana, café, or small restaurant — here is exactly what PocketBillingAI does for your
              business every day.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-5 sm:gap-6">
            {SERVICE_CATEGORIES.map((cat, i) => (
              <motion.div
                key={cat.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="rounded-2xl border border-stone-200/80 bg-white p-6 sm:p-7 shadow-sm"
              >
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-2xl" aria-hidden>{cat.icon}</span>
                  <h3 className="text-lg font-bold text-stone-900">{cat.title}</h3>
                </div>
                <ul className="space-y-2">
                  {cat.items.map((item) => (
                    <li key={item} className="flex items-start gap-2.5 text-sm text-stone-600 leading-relaxed">
                      <span className="flex-shrink-0 text-emerald-600 font-bold mt-0.5">✓</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </section>

        {/* How it works */}
        <section className="bg-indigo-50/50 border-y border-indigo-100 py-14 sm:py-20">
          <div className="max-w-6xl mx-auto px-3 sm:px-6">
            <div className="text-center mb-10 sm:mb-14">
              <h2 className="text-2xl sm:text-4xl font-bold text-stone-900 mb-3">Get started in 3 steps</h2>
              <p className="text-stone-600">No technical knowledge needed — we handle setup for you.</p>
            </div>
            <div className="grid sm:grid-cols-3 gap-6 sm:gap-8">
              {HOW_IT_WORKS.map((step) => (
                <div key={step.step} className="text-center sm:text-left">
                  <div className="inline-flex w-10 h-10 rounded-full bg-indigo-600 text-white font-bold items-center justify-center text-lg mb-4">
                    {step.step}
                  </div>
                  <h3 className="font-semibold text-stone-900 text-lg mb-2">{step.title}</h3>
                  <p className="text-sm text-stone-600 leading-relaxed">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Screenshots */}
        <section className="max-w-6xl mx-auto px-3 sm:px-6 py-14 sm:py-20">
          <div className="text-center mb-10 sm:mb-14">
            <h2 className="text-2xl sm:text-4xl font-bold text-stone-900 mb-3">See it in action</h2>
            <p className="text-stone-600">Clean screens designed for busy shop floors — not cluttered dashboards.</p>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <LiteScreenshotCarousel slides={SCREENSHOTS} />
          </motion.div>
        </section>

        {/* Ideal for */}
        <section className="max-w-6xl mx-auto px-3 sm:px-6 pb-14 sm:pb-20">
          <div className="rounded-3xl bg-gradient-to-br from-indigo-600 to-violet-700 text-white p-8 sm:p-12">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="text-2xl sm:text-3xl font-bold mb-4">Perfect for</h2>
                <ul className="grid sm:grid-cols-2 gap-x-4 gap-y-2">
                  {IDEAL_FOR.map((line) => (
                    <li key={line} className="flex items-center gap-2 text-indigo-100 text-sm sm:text-base">
                      <span className="text-emerald-300 flex-shrink-0">✓</span>
                      {line}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-2xl p-6 border border-white/20">
                <h3 className="font-semibold text-lg mb-2">Why not full RestroAI?</h3>
                <p className="text-indigo-100 text-sm leading-relaxed mb-4">
                  Full RestroAI powers dine-in restaurants with waiters, kitchen displays, table management, and AI
                  analytics. <strong className="text-white">PocketBillingAI</strong> is for shop owners who want fast billing,
                  stock control, udhar tracking, and GST reports — without the full restaurant suite or its cost.
                </p>
                <Link
                  to="/"
                  className="inline-flex text-sm font-semibold text-indigo-900 bg-white hover:bg-indigo-50 px-4 py-2 rounded-lg transition-colors"
                >
                  Explore full RestroAI →
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ — helps search engines understand PocketBillingAI */}
        <section id="faq" className="max-w-3xl mx-auto px-3 sm:px-6 pb-14 sm:pb-16 scroll-mt-24">
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-stone-900 mb-2">
              PocketBillingAI — frequently asked questions
            </h2>
            <p className="text-stone-600 text-sm sm:text-base">
              Billing software for kirana shops, retail stores, and small businesses in India.
            </p>
          </div>
          <div className="space-y-4">
            {SEO_FAQ.map((item) => (
              <details
                key={item.q}
                className="group rounded-xl border border-stone-200 bg-white p-4 sm:p-5 shadow-sm open:shadow-md"
              >
                <summary className="cursor-pointer font-semibold text-stone-900 list-none flex items-center justify-between gap-3">
                  {item.q}
                  <span className="text-indigo-500 text-lg group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="mt-3 text-sm sm:text-base text-stone-600 leading-relaxed">{item.a}</p>
              </details>
            ))}
          </div>
        </section>

        {/* PocketBillingAI contact form */}
        <section id="contact" className="max-w-6xl mx-auto px-3 sm:px-6 pb-16 sm:pb-24 scroll-mt-24">
          <div className="text-center mb-6 sm:mb-8">
            <p className="text-indigo-600 text-xs sm:text-sm font-semibold tracking-widest uppercase mb-2">
              Get started
            </p>
            <h2 className="text-2xl sm:text-3xl font-bold text-stone-900 mb-2">
              Start your PocketBillingAI free trial
            </h2>
            <p className="text-stone-600 text-sm sm:text-base max-w-xl mx-auto">
              Tell us your shop name and type — our team will onboard you with products, billing setup, and training
              within 24 hours.
            </p>
          </div>
          <div className="max-w-lg mx-auto">
            <RestroLiteContactForm />
          </div>
        </section>
      </main>

      <footer className="relative z-10 border-t border-stone-200 bg-white py-8">
        <div className="max-w-6xl mx-auto px-3 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-stone-500">
          <p>© {new Date().getFullYear()} RestroAI · PocketBillingAI</p>
          <div className="flex gap-4">
            <Link to="/" className="hover:text-stone-900">
              Home
            </Link>
            <Link to="/restaurants" className="hover:text-stone-900">
              Restaurants
            </Link>
            <a href="#testimonials" className="hover:text-stone-900">
              Reviews
            </a>
            <a href="#services" className="hover:text-stone-900">
              Features
            </a>
            <Link to="/pocketbillingai/download" className="hover:text-stone-900">
              Download
            </Link>
            <a
              href={GOOGLE_PLAY_APP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-stone-900 font-medium text-indigo-600"
            >
              Google Play
            </a>
            <a href="#contact" className="hover:text-stone-900">
              Contact
            </a>
            <Link to="/privacy-policy" className="hover:text-stone-900">
              Privacy
            </Link>
            <Link to="/pocketbillingai/terms" className="hover:text-stone-900">
              Terms
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
